import { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { faqAPI, type FAQ } from '../services/api';
import { useSiteName } from '../platform/useSiteName';

export default function FAQPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      const res = await faqAPI.getFAQs();
      setFaqs(res.data);
    } catch (error) {
      console.error('加载 FAQ 失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-light-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
              <div className="h-6 w-px bg-light-border" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-light-text">常见问题</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-light-text mb-4">常见问题解答</h1>
          <p className="text-light-muted">以下是用户最常问的问题，希望能帮助到你</p>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-white rounded-2xl border border-light-border" />
            ))}
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-light-muted mx-auto mb-4" />
            <p className="text-light-muted">暂无常见问题</p>
          </div>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-white rounded-2xl border border-light-border shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-light-hover transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-blue/10 text-accent-blue font-semibold flex items-center justify-center text-sm">
                      {index + 1}
                    </span>
                    <span className="font-medium text-light-text text-lg">{faq.question}</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-light-muted transition-transform flex-shrink-0 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 pl-18">
                    <div className="pl-12 text-light-muted leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-light-border mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-light-muted">
            <p>© 2024 AI Image Generator. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
